import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { FrontendUpdateRequiredComponent } from '../../Dialog/frontend-update-required/frontend-update-required.component';
import { BUILD_INFO } from '../../generated/build-info';
import { UserServiceService } from '../api/user-service.service';

export interface FrontendVersionManifest {
  buildId: string;
  clientVersion: string;
}

export interface FrontendReleaseFeature {
  icon: string;
  description: string;
}

export interface FrontendReleaseNotes {
  title: string;
  summary?: string;
  features: FrontendReleaseFeature[];
}

export interface FrontendReleaseManifest extends FrontendVersionManifest, FrontendReleaseNotes {}

interface PendingUpdate {
  buildId: string;
  attempts: number;
  manual: boolean;
}

@Injectable({ providedIn: 'root' })
export class FrontendUpdateService implements OnDestroy {
  private static readonly POLL_INTERVAL_MS = 5 * 60 * 1000;
  private static readonly MAX_RELOAD_ATTEMPTS = 1;
  private static readonly PENDING_KEY = 'frontend.pendingUpdate';
  private static readonly ACK_PENDING_KEY = 'frontend.ackPendingBuild';
  private static readonly DIALOG_ID = 'frontend-update-required';
  private checking = false;
  private started = false;
  private pollId: ReturnType<typeof setInterval> | null = null;
  private dialogRef: MatDialogRef<FrontendUpdateRequiredComponent> | null = null;
  private lastManifest: FrontendReleaseManifest | null = null;

  constructor(
    private http: HttpClient,
    private userService: UserServiceService,
    private dialog: MatDialog,
    private ngZone: NgZone,
  ) {}

  start(): void {
    if (this.started) {
      this.checkRequiredReload();
      return;
    }
    this.started = true;
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('focus', this.handleActivation);
      window.addEventListener('online', this.handleActivation);
      window.addEventListener('frontend-upgrade-required', this.handleUpgradeRequired);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      this.pollId = window.setInterval(
        () => this.checkRequiredReload(),
        FrontendUpdateService.POLL_INTERVAL_MS,
      );

      void this.finishPendingUpdate().finally(() => this.checkRequiredReload());
    });
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    window.removeEventListener('focus', this.handleActivation);
    window.removeEventListener('online', this.handleActivation);
    window.removeEventListener('frontend-upgrade-required', this.handleUpgradeRequired);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    if (this.pollId) clearInterval(this.pollId);
    this.pollId = null;
  }

  checkRequiredReload(): void {
    this.ngZone.runOutsideAngular(() => this.checkRequiredReloadOutsideAngular());
  }

  private checkRequiredReloadOutsideAngular(): void {
    if (this.checking || this.dialogRef) return;
    this.checking = true;

    void this.loadManifest()
      .then(async (manifest) => {
        this.lastManifest = manifest;
        if (manifest.buildId !== BUILD_INFO.buildId) {
          this.openUpdateDialog(manifest, false);
          return;
        }

        if (sessionStorage.getItem('authenticated') === 'true') {
          const usuario = await firstValueFrom(this.userService.obtenerUsuarioLogeado());
          if (usuario?.requiereRecargaFrontend) {
            if (sessionStorage.getItem(FrontendUpdateService.ACK_PENDING_KEY) === BUILD_INFO.buildId) {
              void this.retryAcknowledgement();
            } else {
              this.openUpdateDialog(manifest, true);
            }
          }
        }
      })
      .catch(() => {
        // Una falla de red no debe interrumpir la aplicación; se reintentará luego.
      })
      .finally(() => {
        this.checking = false;
      });
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private readonly handleActivation = (): void => this.checkRequiredReload();
  private readonly handleUpgradeRequired = (): void => {
    if (this.lastManifest) this.openUpdateDialog(this.lastManifest, false);
    else this.checkRequiredReload();
  };
  private readonly handleVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') this.checkRequiredReload();
  };

  private async loadManifest(): Promise<FrontendReleaseManifest> {
    const cacheBuster = Date.now();
    const options = { headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } };
    const [manifest, releaseNotes] = await Promise.all([
      firstValueFrom(this.http.get<FrontendVersionManifest>(`/version.json?v=${cacheBuster}`, options)),
      firstValueFrom(this.http.get<FrontendReleaseNotes>(`/release-notes.json?v=${cacheBuster}`, options))
        .catch(() => null),
    ]);
    return {
      ...manifest,
      title: releaseNotes?.title || 'Actualización disponible',
      summary: releaseNotes?.summary,
      features: releaseNotes?.features || [],
    };
  }

  private openUpdateDialog(manifest: FrontendReleaseManifest, manual: boolean): void {
    this.ngZone.run(() => this.openUpdateDialogInAngular(manifest, manual));
  }

  private openUpdateDialogInAngular(manifest: FrontendReleaseManifest, manual: boolean): void {
    const existingDialog = this.dialog.getDialogById(FrontendUpdateService.DIALOG_ID);
    if (this.dialogRef || existingDialog) {
      this.dialogRef = existingDialog as MatDialogRef<FrontendUpdateRequiredComponent>;
      return;
    }
    const current = this.readPending();
    const pending: PendingUpdate = current?.buildId === manifest.buildId
      ? { ...current, manual: current.manual || manual }
      : { buildId: manifest.buildId, attempts: 0, manual };
    this.writePending(pending);

    this.dialogRef = this.dialog.open(FrontendUpdateRequiredComponent, {
      id: FrontendUpdateService.DIALOG_ID,
      width: 'min(560px, calc(100vw - 24px))',
      maxWidth: 'calc(100vw - 24px)',
      maxHeight: 'calc(100dvh - 24px)',
      panelClass: 'frontend-update-dialog-panel',
      disableClose: true,
      closeOnNavigation: false,
      data: {
        ...manifest,
        retryLimitReached: pending.attempts >= FrontendUpdateService.MAX_RELOAD_ATTEMPTS,
      },
    });

    this.dialogRef.afterClosed().subscribe((confirmed) => {
      this.dialogRef = null;
      if (confirmed !== true) return;
      const latest = this.readPending() ?? pending;
      this.writePending({ ...latest, attempts: latest.attempts + 1 });
      void this.navigateToRelease(latest.buildId);
    });
  }

  private async finishPendingUpdate(): Promise<void> {
    const pending = this.readPending();
    if (!pending || pending.buildId !== BUILD_INFO.buildId) return;

    sessionStorage.removeItem(FrontendUpdateService.PENDING_KEY);
    if (pending.manual && sessionStorage.getItem('authenticated') === 'true') {
      sessionStorage.setItem(FrontendUpdateService.ACK_PENDING_KEY, BUILD_INFO.buildId);
      await this.retryAcknowledgement();
    }
  }

  private async retryAcknowledgement(): Promise<void> {
    if (sessionStorage.getItem('authenticated') !== 'true') return;
    try {
      await firstValueFrom(this.userService.ackFrontendUpdate());
      sessionStorage.removeItem(FrontendUpdateService.ACK_PENDING_KEY);
    } catch {
      // El build ya es correcto. El ACK se reintentará sin forzar otra recarga.
    }
  }

  private async navigateToRelease(buildId: string): Promise<void> {
    const target = new URL(window.location.href);
    target.searchParams.set('__build', buildId);
    target.searchParams.set('__reload', String(Date.now()));
    window.location.replace(target.toString());
  }

  private readPending(): PendingUpdate | null {
    try {
      const value = sessionStorage.getItem(FrontendUpdateService.PENDING_KEY);
      return value ? JSON.parse(value) as PendingUpdate : null;
    } catch {
      return null;
    }
  }

  private writePending(pending: PendingUpdate): void {
    sessionStorage.setItem(FrontendUpdateService.PENDING_KEY, JSON.stringify(pending));
  }
}
